import type { MafiaEvent, MafiaPublicState, MafiaRole } from "@topzle/game-mafia";
import {
  Check,
  Clock3,
  Crosshair,
  Eye,
  HeartPulse,
  MoonStar,
  Search,
  ShieldCheck,
  Skull,
  Sparkles,
  Sun,
  Sunrise,
  UserRound,
  Users,
  VenetianMask,
  Vote,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { LoadingButton } from "./LoadingButton";

interface MafiaParticipant {
  id: string;
  displayName: string;
  avatar?: string;
}

const roleDetails: Record<
  MafiaRole,
  { label: string; team: string; instruction: string; icon: typeof VenetianMask }
> = {
  mafia: {
    label: "Mafia",
    team: "The Syndicate",
    instruction: "Stay hidden by day. Choose a Town player to eliminate each night.",
    icon: VenetianMask,
  },
  detective: {
    label: "Detective",
    team: "Town",
    instruction: "Investigate one player each night. Only you see the result.",
    icon: Search,
  },
  doctor: {
    label: "Doctor",
    team: "Town",
    instruction: "Protect one living player each night. You may protect yourself.",
    icon: HeartPulse,
  },
  citizen: {
    label: "Citizen",
    team: "Town",
    instruction: "Listen carefully, find contradictions, and expose the Mafia by vote.",
    icon: UserRound,
  },
};

const phaseCopy: Record<MafiaPublicState["phase"], { eyebrow: string; title: string }> = {
  role_reveal: { eyebrow: "CLASSIFIED", title: "Learn your role" },
  night: { eyebrow: "THE TOWN SLEEPS", title: "Night actions" },
  dawn: { eyebrow: "DAWN REPORT", title: "The town wakes" },
  discussion: { eyebrow: "OPEN DISCUSSION", title: "Who can you trust?" },
  voting: { eyebrow: "TOWN BALLOT", title: "Choose carefully" },
  finished: { eyebrow: "CASE CLOSED", title: "The truth is out" },
};

const skylineBuildings = [
  "clocktower",
  "apartments-a",
  "apartments-b",
  "hotel",
  "cinema",
  "town-hall",
  "library",
  "apartments-c",
  "station",
  "cafe",
  "apartments-d",
  "theatre",
  "warehouse",
] as const;

function secondsRemaining(deadlineAt: number | null, now: number): number | null {
  if (!deadlineAt) return null;
  return Math.max(0, Math.ceil((deadlineAt - now) / 1_000));
}

export function MafiaBoard({
  state,
  participantId,
  participants,
  pending,
  serverError,
  playAgain,
  onAction,
}: {
  state: MafiaPublicState;
  participantId: string;
  participants: MafiaParticipant[];
  pending: boolean;
  serverError: string | null;
  playAgain?: React.ReactNode;
  onAction: (event: MafiaEvent) => boolean;
}) {
  const [now, setNow] = useState(Date.now());
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, []);
  const phaseKey = `${state.day}:${state.phase}`;
  useEffect(() => {
    if (phaseKey) setSelectedTarget(null);
  }, [phaseKey]);

  const participantById = useMemo(
    () => new Map(participants.map((participant) => [participant.id, participant])),
    [participants],
  );
  const role = state.ownRole;
  const details = role ? roleDetails[role] : null;
  const RoleIcon = details?.icon ?? Eye;
  const alive = state.aliveIds.includes(participantId);
  const acknowledged = state.roleAcknowledgedIds.includes(participantId);
  const voted = participantId in state.votes;
  const readyToVote = state.readyToVoteIds.includes(participantId);
  const remaining = secondsRemaining(state.deadlineAt, now);
  const phase = phaseCopy[state.phase];
  const nightRoleCanAct = role === "mafia" || role === "doctor" || role === "detective";
  const targetMode =
    alive && state.phase === "night" && nightRoleCanAct
      ? "night"
      : alive && state.phase === "voting" && !voted
        ? "vote"
        : null;

  const playerName = (playerId: string | null) =>
    playerId ? (participantById.get(playerId)?.displayName ?? "Unknown player") : "No one";
  const canTarget = (playerId: string) => {
    if (!targetMode || !state.aliveIds.includes(playerId)) return false;
    if (targetMode === "vote") return playerId !== participantId;
    if (role === "doctor") return true;
    if (playerId === participantId) return false;
    if (role === "mafia" && state.mafiaTeamIds.includes(playerId)) return false;
    return true;
  };
  const submitTarget = () => {
    if (!selectedTarget) return;
    const sent = onAction(
      targetMode === "night"
        ? { type: "night_action", targetId: selectedTarget }
        : { type: "cast_vote", targetId: selectedTarget },
    );
    if (sent) setSelectedTarget(null);
  };

  return (
    <section className={`mafia-board mafia-${state.phase}`}>
      <div className="mafia-sky" aria-hidden="true">
        <span className="mafia-moon">
          <MoonStar />
        </span>
        <span className="mafia-sun">
          <Sun />
        </span>
        <span className="mafia-cloud cloud-one" />
        <span className="mafia-cloud cloud-two" />
        <div className="mafia-skyline">
          {skylineBuildings.map((building) => (
            <i key={building} />
          ))}
        </div>
      </div>

      <header className="mafia-phase-header">
        <div>
          <span className="eyebrow">
            {phase.eyebrow} · DAY {state.day}
          </span>
          <h2>{phase.title}</h2>
        </div>
        {remaining !== null && state.phase !== "finished" && (
          <div className={`mafia-timer ${remaining <= 8 ? "urgent" : ""}`}>
            <Clock3 />
            <strong>{remaining}</strong>
            <span>seconds</span>
          </div>
        )}
      </header>

      <div className="mafia-game-grid">
        <aside className={`mafia-role-card role-${role ?? "spectator"}`}>
          <div className="mafia-role-seal">
            <RoleIcon />
          </div>
          <span className="eyebrow">YOUR SECRET DOSSIER</span>
          <h3>{details?.label ?? "Spectator"}</h3>
          <small>{details?.team ?? "Watching the mystery"}</small>
          <p>{details?.instruction ?? "Observe the town without taking an action."}</p>
          {role === "mafia" && state.mafiaTeamIds.length > 1 && (
            <div className="mafia-team-list">
              <Users />
              <span>
                Partners:{" "}
                {state.mafiaTeamIds
                  .filter((id) => id !== participantId)
                  .map(playerName)
                  .join(", ")}
              </span>
            </div>
          )}
          {!alive && role && (
            <div className="mafia-eliminated-note">
              <Skull /> You are now observing the case.
            </div>
          )}
        </aside>

        <main className="mafia-town-square">
          <div className="mafia-player-ring">
            {state.players.map((playerId, index) => {
              const player = participantById.get(playerId);
              const isAlive = state.aliveIds.includes(playerId);
              const isSelf = playerId === participantId;
              const revealedRole = state.revealedRoles[playerId];
              const voteCount = Object.values(state.votes).filter(
                (target) => target === playerId,
              ).length;
              const mafiaVotes = Object.values(state.mafiaNightVotes).filter(
                (target) => target === playerId,
              ).length;
              return (
                <button
                  type="button"
                  className={`mafia-player ${isAlive ? "alive" : "eliminated"} ${
                    selectedTarget === playerId ? "selected" : ""
                  } ${canTarget(playerId) ? "targetable" : ""}`}
                  style={{ "--seat": index } as React.CSSProperties}
                  onClick={() => canTarget(playerId) && setSelectedTarget(playerId)}
                  disabled={!canTarget(playerId) || pending}
                  aria-pressed={selectedTarget === playerId}
                  key={playerId}
                >
                  <span className="mafia-avatar">
                    {player?.avatar ? (
                      <img src={player.avatar} alt="" />
                    ) : (
                      <b>{player?.displayName.slice(0, 1) ?? "?"}</b>
                    )}
                    {!isAlive && <Skull className="mafia-skull" />}
                  </span>
                  <strong>{isSelf ? "You" : (player?.displayName ?? "Player")}</strong>
                  <small>
                    {revealedRole
                      ? roleDetails[revealedRole].label
                      : isAlive
                        ? "Under suspicion"
                        : "Eliminated"}
                  </small>
                  {state.phase === "voting" && voteCount > 0 && (
                    <em>
                      {voteCount} vote{voteCount === 1 ? "" : "s"}
                    </em>
                  )}
                  {role === "mafia" && state.phase === "night" && mafiaVotes > 0 && (
                    <em>{mafiaVotes} marked</em>
                  )}
                  {canTarget(playerId) && (
                    <span className="mafia-target-reticle">
                      <Crosshair />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mafia-narrator" aria-live="polite">
            {state.phase === "role_reveal" ? (
              <>
                <ShieldCheck />
                <div>
                  <strong>Your role is private.</strong>
                  <span>Remember it, then lock your dossier.</span>
                </div>
                {!acknowledged && role && (
                  <LoadingButton
                    loading={pending}
                    onClick={() => onAction({ type: "acknowledge_role" })}
                  >
                    <Check /> I know my role
                  </LoadingButton>
                )}
                {acknowledged && (
                  <span className="mafia-locked">
                    <Check /> Locked
                  </span>
                )}
              </>
            ) : state.phase === "night" ? (
              <>
                <MoonStar />
                <div>
                  <strong>
                    {nightRoleCanAct && alive
                      ? "Make your move in secret."
                      : "Keep your eyes closed."}
                  </strong>
                  <span>
                    {state.ownNightTargetId
                      ? `Choice locked: ${playerName(state.ownNightTargetId)}`
                      : nightRoleCanAct && alive
                        ? "Choose one highlighted player."
                        : "The night roles are acting."}
                  </span>
                </div>
                {targetMode === "night" && selectedTarget && (
                  <LoadingButton loading={pending} onClick={submitTarget}>
                    <MoonStar /> Confirm {playerName(selectedTarget)}
                  </LoadingButton>
                )}
              </>
            ) : state.phase === "dawn" ? (
              <>
                <Sunrise />
                <div>
                  <strong>
                    {state.nightVictimId
                      ? `${playerName(state.nightVictimId)} was lost in the night.`
                      : state.nightSaved
                        ? "Someone was attacked, but the Doctor intervened."
                        : "The town had a quiet night."}
                  </strong>
                  <span>Study the report, then continue when you are ready to discuss.</span>
                </div>
                {alive && (
                  <LoadingButton
                    loading={pending}
                    onClick={() => onAction({ type: "advance_phase" })}
                  >
                    <Sunrise /> Continue
                  </LoadingButton>
                )}
              </>
            ) : state.phase === "discussion" ? (
              <>
                <Users />
                <div>
                  <strong>Talk, question, and compare stories.</strong>
                  <span>
                    {state.readyToVoteIds.length}/{state.aliveIds.length} living players are ready
                    to vote.
                  </span>
                </div>
                {alive && !readyToVote && (
                  <LoadingButton
                    loading={pending}
                    onClick={() => onAction({ type: "ready_to_vote" })}
                  >
                    <Vote /> Ready to vote
                  </LoadingButton>
                )}
              </>
            ) : state.phase === "voting" ? (
              <>
                <Vote />
                <div>
                  <strong>
                    {voted ? "Your ballot is locked." : "Select the player you suspect."}
                  </strong>
                  <span>
                    {Object.keys(state.votes).length}/{state.aliveIds.length} votes cast.
                  </span>
                </div>
                {targetMode === "vote" && selectedTarget && (
                  <LoadingButton loading={pending} onClick={submitTarget}>
                    <Vote /> Vote for {playerName(selectedTarget)}
                  </LoadingButton>
                )}
                {targetMode === "vote" && !selectedTarget && (
                  <LoadingButton
                    className="button button-ghost compact"
                    loading={pending}
                    onClick={() => onAction({ type: "cast_vote", targetId: null })}
                  >
                    Abstain
                  </LoadingButton>
                )}
              </>
            ) : (
              <>
                <Sparkles />
                <div>
                  <strong>
                    {state.winner === "town"
                      ? "Town wins — every Mafia member was exposed."
                      : "Mafia wins — the town has lost control."}
                  </strong>
                  <span>Every hidden role is now revealed.</span>
                </div>
                {playAgain}
              </>
            )}
          </div>
        </main>

        <aside className="mafia-caseboard">
          <span className="eyebrow">CASE NOTES</span>
          {role === "detective" && state.investigations.length > 0 ? (
            <div className="mafia-investigations">
              {state.investigations.map((result) => (
                <div
                  className={result.isMafia ? "guilty" : "clear"}
                  key={`${result.day}-${result.targetId}`}
                >
                  {result.isMafia ? <VenetianMask /> : <ShieldCheck />}
                  <span>
                    <strong>{playerName(result.targetId)}</strong>
                    <small>
                      {result.isMafia ? "Mafia" : "Not Mafia"} · Night {result.day}
                    </small>
                  </span>
                </div>
              ))}
            </div>
          ) : state.history.length > 0 ? (
            <div className="mafia-history">
              {[...state.history]
                .reverse()
                .slice(0, 5)
                .map((entry) => (
                  <div key={`${entry.day}-${entry.kind}`}>
                    {entry.kind === "night" ? <MoonStar /> : <Vote />}
                    <span>
                      <strong>
                        {entry.kind === "night" ? `Night ${entry.day}` : `Day ${entry.day} vote`}
                      </strong>
                      <small>
                        {entry.eliminatedId
                          ? `${playerName(entry.eliminatedId)} eliminated`
                          : entry.saved
                            ? "Doctor saved the target"
                            : entry.tied
                              ? "Tie · no elimination"
                              : "No elimination"}
                      </small>
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <div className="mafia-empty-notes">
              <Eye />
              <p>No public clues yet. Watch how people speak and vote.</p>
            </div>
          )}
        </aside>
      </div>
      {serverError && <p className="game-error mafia-error">{serverError}</p>}
    </section>
  );
}
